/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from 'react';

const Autocomplete = ({
  options = [],
  placeholder = 'Select...',
  onSelect,
  name, // Form field name
  idKey = 'id', // Key for identifier (id or bookId)
  displayKey = 'title' // Key for display text
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce filtering for performance
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Get display text for an option
  const getDisplayText = useCallback(
    (option) => {
      const id = option[idKey] || '';
      const title = option[displayKey] || '';
      // Only include parentheses for idKey === 'bookId' (result2)
      return idKey === 'bookId' && id ? `${title} (${id})` : title;
    },
    [idKey, displayKey]
  );

  // Filter options
  const filterOptions = useCallback(
    (value) => {
      if (!options?.length) {
        setFilteredOptions([]);
        return;
      }
      const filtered = options.filter((option) => getDisplayText(option).toLowerCase().includes(value.toLowerCase()));
      setFilteredOptions(filtered);
    },
    [options, getDisplayText]
  );

  const debouncedFilter = useCallback(debounce(filterOptions, 300), [filterOptions]);

  // Handle input change
  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSelectedValue(null);
    debouncedFilter(value);
    setShowDropdown(true);
  };

  // Handle focus
  const handleFocus = () => {
    if (!selectedValue) {
      filterOptions(inputValue);
      setShowDropdown(true);
    }
  };

  // Handle selection
  const handleSelect = (option) => {
    const displayText = getDisplayText(option);
    const value = {
      id: option[idKey] || '',
      title: option[displayKey] || ''
    };
    setSelectedValue(displayText);
    setInputValue('');
    setShowDropdown(false);
    setHoveredIndex(-1);
    onSelect?.(name, value);
    inputRef.current?.focus();
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedValue(null);
    setInputValue('');
    onSelect?.(name, { id: '', title: '' });
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (selectedValue) return;
    setShowDropdown(!showDropdown);
    if (!showDropdown) inputRef.current?.focus();
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || !filteredOptions.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHoveredIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHoveredIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (hoveredIndex >= 0) {
          handleSelect(filteredOptions[hoveredIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHoveredIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
        setHoveredIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', width: 'fit-content' }}
      role="combobox"
      aria-expanded={showDropdown}
    >
      <div style={{ position: 'relative' }}>
        {selectedValue ? (
          <div
            style={{
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'rgb(163 163 163)',
              fontSize: '14px',
              height: '24px',
              width: '220px'
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis'
              }}
            >
              {selectedValue}
            </span>
            <span
              onClick={clearSelection}
              style={{
                cursor: 'pointer',
                marginLeft: '8px',
                fontWeight: 'bold'
              }}
              role="button"
              aria-label="Clear selection"
            >
              ×
            </span>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              width: '200px',
              padding: '8px',
              paddingRight: '30px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              height: '24px'
            }}
            aria-autocomplete="list"
            aria-controls="autocomplete-list"
          />
        )}

        {/* Dropdown icon */}
        {!selectedValue && (
          <span
            onClick={toggleDropdown}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              fontSize: '12px',
              color: '#888'
            }}
            role="button"
            aria-label={showDropdown ? 'Close dropdown' : 'Open dropdown'}
          >
            {showDropdown ? '▲' : '▼'}
          </span>
        )}
      </div>

      {/* Dropdown list */}
      {showDropdown && !selectedValue && (
        <ul
          id="autocomplete-list"
          style={{
            position: 'absolute',
            width: '99%',
            maxHeight: '180px',
            overflowY: 'auto',
            backgroundColor: 'rgb(0 0 0)',
            border: '1px solid #ccc',
            borderTop: 'none',
            zIndex: 1,
            margin: 0,
            padding: 0,
            listStyle: 'none'
          }}
          role="listbox"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option[idKey] || index}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(-1)}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  padding: '8px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  backgroundColor: hoveredIndex === index ? '#f0f0f0' : 'rgb(0 0 0)',
                  color: hoveredIndex === index ? 'rgb(0 0 0)' : '#fff',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  textAlign: 'left'
                }}
                role="option"
                aria-selected={hoveredIndex === index}
              >
                {getDisplayText(option)}
              </li>
            ))
          ) : (
            <li
              style={{
                padding: '8px',
                color: '#888',
                textAlign: 'center'
              }}
              role="presentation"
            >
              No results found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default Autocomplete;
